// A folha de rota de uma rota JÁ SALVA, montada de novo a partir do que está
// gravado.
//
// POR QUE EXISTE
//
// A mensagem do WhatsApp nascia num lugar só: o fim da conferência, na Central
// de Inadimplência, com o que aquela tela tinha em memória. Fechou a tela,
// perdeu a mensagem. Quem abre a rota depois — no celular, no dia seguinte, ou
// para reenviá-la a outro cobrador — não tinha de onde tirá-la, e o caminho que
// sobrava era remontar a rota do zero. É assim que nascem as duplicatas que o
// gestor reclamou ("Cobrança geral" três vezes em 10/09).
//
// O FORMATO NÃO É DECIDIDO AQUI
//
// Quem escreve o texto continua sendo `mensagemCobranca.ts`: o formato foi
// especificado pelo gestor e vale para as duas telas. Aqui só se resolve DE ONDE
// vem cada campo quando a fonte é a rota salva, e não a lista da Central.
//
// PURO DE PROPÓSITO
//
// Nada aqui vai ao banco — quem busca é a tela, com as funções que a conferência
// já usa (`cadastrosPorUcId`, `faturasAtuaisDasParadas`, `termosPorUc`,
// `ultimaTratativaPorCpf`). Assim o texto é testável sem subir o Supabase.

import { ucsDaParada } from './agrupamentoRota.ts';
import {
    cabecalhoRota, escolherFormatoMensagem, mensagemRota,
    type ParadaMensagem,
} from './mensagemCobranca.ts';
import { enderecoUcTexto, telefonesUc, urlNavegacaoUc } from './enderecoUc.ts';
import { navUrlCoordenada } from './navegacaoMaps.ts';
import type { UcEndereco } from './enderecoUc';
import type { AppNavegacao } from './navegacaoMaps';
import type { TermoAdesao } from './termoUc';
import type { EventoTratativa, FaturaEmAberto, ParadaSalva, RotaSalva } from './rotaCobranca';

/** Tudo o que a tela leu do banco para montar a mensagem. */
export interface FontesDaMensagem {
    /** Origem do app, sem barra no fim — `baseUrlPublica()`. */
    baseUrl: string;
    /** Em qual app os links de navegação abrem. Ver navegacaoMaps.ts. */
    app?: AppNavegacao;
    /** Cadastro por id de UC — `cadastrosPorUcId`. */
    cadastros: Map<number, UcEndereco>;
    /** Faturas em aberto HOJE, por id de parada — `faturasAtuaisDasParadas`. */
    faturasPorParada: Map<string, FaturaEmAberto[]>;
    /** Chave: CPF/CNPJ só com dígitos, como `ultimaTratativaPorCpf` devolve. */
    tratativas?: Map<string, EventoTratativa>;
    /** Chave: id da parada — é o que a tela passa como `chave` para `termosPorUc`. */
    termos?: Map<string, TermoAdesao>;
}

export interface MensagemDaRota {
    texto: string;
    /** Saiu na versão curta (não cabia inteira no link do WhatsApp). */
    compacta: boolean;
}

const somaValor = (faturas: FaturaEmAberto[]): number =>
    Math.round(faturas.reduce((s, f) => s + (Number(f.valor) || 0), 0) * 100) / 100;

/** O link de navegação da parada, com a correção da conferência na frente.
 *
 *  O pino arrastado à mão vale mais que qualquer coisa que o geocoder tenha dito
 *  — foi gente olhando o mapa. Ele é gravado nos dois lugares (na parada e na
 *  UC), e aqui o da parada ganha: se a gravação na UC falhou, a correção desta
 *  rota continua valendo. */
const linkDaParada = (
    p: ParadaSalva,
    cadastro: UcEndereco | null,
    app: AppNavegacao,
): string => {
    if (p.endereco_situacao === 'corrigido' && p.endereco_lat != null && p.endereco_lng != null) {
        return navUrlCoordenada({ lat: Number(p.endereco_lat), lng: Number(p.endereco_lng) }, app);
    }
    return urlNavegacaoUc(cadastro, null, app);
};

/**
 * Uma parada salva no formato que a mensagem espera.
 *
 * Quantidade e valor vêm da RELEITURA, não do número congelado na geração: entre
 * montar e mandar a rota o cliente pode ter pago, e a folha de rota é para bater
 * na porta. Quando a releitura não devolve nada para a parada, o congelado
 * assume — sem fatura em aberto pode ser pagamento, mas também pode ser a UC
 * gravada com o outro número da Equatorial, e zerar a parada por isso mandaria
 * o cobrador embora de uma porta que deve.
 */
export const paradaParaMensagem = (p: ParadaSalva, fontes: FontesDaMensagem): ParadaMensagem => {
    const app = fontes.app ?? 'google';
    const cadastro = (p.unidade_consumidora_id != null
        ? fontes.cadastros.get(p.unidade_consumidora_id)
        : null) ?? null;

    const faturas = fontes.faturasPorParada.get(p.id) ?? [];
    const relido = faturas.length > 0;

    const [, ...agrupadas] = ucsDaParada(p);
    const doGrupo = (ucCodigo: string) => faturas.filter(f => (f.uc_ref ?? p.uc_codigo) === ucCodigo);

    // UC juntada que zerou some da lista: "+ UC X: 0 faturas — R$ 0,00" manda o
    // cobrador cobrar o que ninguém deve.
    const ucsExtras = agrupadas
        .map(u => (relido
            ? { ucCodigo: u.uc_codigo, qtdFaturas: doGrupo(u.uc_codigo).length, valorTotal: somaValor(doGrupo(u.uc_codigo)) }
            : { ucCodigo: u.uc_codigo, qtdFaturas: Number(u.qtd_faturas) || 0, valorTotal: Number(u.valor_total) || 0 }))
        .filter(u => u.qtdFaturas > 0);

    const vencimentos = faturas
        .map(f => f.vencimento)
        .filter((v): v is string => !!v)
        .sort();

    const cpf = String(p.cpf_cnpj_cliente || '').replace(/\D/g, '');

    return {
        posicao: p.posicao,
        ucCodigo: p.uc_codigo,
        titular: cadastro?.titular_fatura ?? null,
        clienteNome: p.cliente_nome || '',
        telefones: telefonesUc(cadastro),
        qtdFaturas: relido ? faturas.length : Number(p.qtd_faturas) || 0,
        valorTotal: relido ? somaValor(faturas) : Number(p.valor_total) || 0,
        vencMaisAntigo: vencimentos[0] ?? null,
        enderecoTexto: enderecoUcTexto(cadastro) || p.endereco_label || null,
        pdfFatura: faturas.find(f => !!f.pdf_fatura)?.pdf_fatura ?? null,
        urlMaps: linkDaParada(p, cadastro, app),
        faturaIds: faturas.map(f => f.id),
        ucsExtras,
        termo: fontes.termos?.get(p.id) ?? null,
        ultimaTratativa: (cpf && fontes.tratativas?.get(cpf)) || null,
    };
};

/**
 * A mensagem inteira da rota salva — cabeçalho, chegada e um bloco por parada.
 *
 * Só entra o que FALTA visitar, na ordem atual. A mensagem é reenviada no meio
 * do dia — depois de reordenar a partir de onde o cobrador está —, e mandar de
 * novo as portas já batidas o fazia rolar a folha inteira até achar a próxima.
 * O número de cada parada continua o da rota: é o mesmo do card e do `?parada=N`.
 *
 * Mesma escolha de formato da montagem: o cheio é o padrão e o curto só entra
 * quando ele cabe no link do WhatsApp e o cheio não (ver `escolherFormatoMensagem`).
 */
export const montarMensagemDaRota = (
    rota: RotaSalva,
    fontes: FontesDaMensagem,
): MensagemDaRota => {
    const todas = rota.paradas || [];
    const paradas = todas
        .filter(p => p.status === 'pendente')
        .sort((a, b) => a.posicao - b.posicao);
    const blocos = paradas.map(p => paradaParaMensagem(p, fontes));

    const cabecalho = cabecalhoRota({
        cidade: rota.cidade,
        data: rota.data,
        paradas: blocos.length,
        valorTotal: blocos.reduce((s, b) => s + b.valorTotal, 0),
    });
    const feitas = todas.length - paradas.length;
    const jaFeitas = feitas > 0
        ? `\n${feitas} já ${feitas === 1 ? 'visitada ficou' : 'visitadas ficaram'} de fora.`
        : '';
    // A chegada vai escrita: no Apple Maps e no Waze o link não a carrega.
    const chegada = rota.destino_label ? `\nChegada: ${rota.destino_label}` : '';

    const opcoes = { baseUrl: fontes.baseUrl, rotaId: rota.id, compacta: false };
    const topo = `${cabecalho}${jaFeitas}${chegada}`;
    const cheia = `${topo}\n\n${mensagemRota(blocos, opcoes)}`;
    const curta = `${topo}\n\n${mensagemRota(blocos, { ...opcoes, compacta: true })}`;
    const compacta = escolherFormatoMensagem(cheia, curta) === 'curta';

    return { texto: compacta ? curta : cheia, compacta };
};
